/**
 * mp4-track-spans.js — read how long each TRACK of an mp4 actually is,
 * straight out of the container header. No FFmpeg, no decode.
 *
 * Why this exists
 * ---------------
 * A stream-copy concat (`-f concat -c copy`) is a PREDICTION: the caller
 * inspects the inputs, decides they are compatible, and then trusts the
 * result. When the prediction is wrong the mux still succeeds — exit code 0,
 * a playable file, the right total duration in the container header — and the
 * damage lands inside one track. The user sees «изображение стоит на месте, а
 * звук идёт»: the audio track spans the whole timeline while the video track
 * ended early, so the player holds the last decoded frame.
 *
 * Measured 2026-08-30 on /video-merge (report #583, Alexe). Six 8-second mp4
 * clips: three straight from the camera (video timescale 12288) and three that
 * had been through our own /video-flip, whose WebCodecs muxer writes timescale
 * 24. FFmpeg's concat demuxer could not reconcile the two timebases, clamped
 * every packet after the boundary to `previous DTS + 1 tick`, and produced:
 *
 *     video track  24.000 s   (all 1152 frames present, 577 of them crammed
 *                              into 0.047 s)
 *     audio track  48.085 s
 *     container    48.085 s   ← the only number `ffmpeg -i` prints
 *
 * The container duration is the MAXIMUM over tracks, so it looks healthy. The
 * per-track spans are the only place the corruption is visible before playback,
 * which is what this module reads.
 *
 * API
 * ---
 *   window.__mp4TrackSpans(bytes)   // Uint8Array / ArrayBuffer of a whole mp4
 *     → { video: {timescale, durationSec, sampleCount} | null,
 *         audio: {timescale, durationSec, sampleCount} | null,
 *         movieDurationSec: 48.085 }
 *     → null when the bytes are not an mp4 we can read (webm/mkv/avi output,
 *       fragmented mp4 with an empty stbl, truncated moov, …).
 *
 * `null` means UNKNOWN, never "broken" — every caller must treat it as
 * "carry on as before". A container we cannot parse is not evidence of damage.
 *
 * ⚠️ durationSec comes from `mdhd`, which is the MEDIA duration. The presented
 * duration can differ when an edit list (`elst`) trims or delays the track;
 * for the concat-integrity question that difference is noise (fractions of a
 * frame) next to the failure it detects (half the timeline).
 */
(function () {
'use strict';
if (window.__mp4TrackSpans) return;

function u32(b, p) { return (b[p] << 24 | b[p + 1] << 16 | b[p + 2] << 8 | b[p + 3]) >>> 0; }
function str4(b, p) { return String.fromCharCode(b[p], b[p + 1], b[p + 2], b[p + 3]); }

/* Boxes whose payload is a list of further boxes. Anything not listed is a
   leaf and gets skipped by size — so an unknown box can never derail the walk. */
var CONTAINERS = { moov: 1, trak: 1, mdia: 1, minf: 1, stbl: 1, edts: 1 };

window.__mp4TrackSpans = function (bytes) {
    var b;
    try {
        b = (bytes instanceof Uint8Array) ? bytes : new Uint8Array(bytes);
    } catch (_) { return null; }
    if (!b || b.length < 16) return null;

    var out = { video: null, audio: null, movieDurationSec: 0 };
    var seenMoov = false;

    /* One pass. `trak` opens a fresh per-track accumulator; hdlr names the
       kind, mdhd gives the timebase, stsz/stts give the sample count. The
       accumulator is committed when the trak box closes, so a track missing
       any of the three simply never lands. */
    (function walk(start, end, track) {
        var p = start;
        while (p + 8 <= end) {
            var size = u32(b, p), type = str4(b, p + 4), hdr = 8;
            if (size === 1) {
                if (p + 16 > end) return;
                /* 64-bit size. Beyond 2^53 the arithmetic stops being exact, but
                   such a box cannot fit in a Uint8Array anyway. */
                size = u32(b, p + 8) * 4294967296 + u32(b, p + 12);
                hdr = 16;
            }
            if (size < hdr) return;                 // malformed — stop, keep what we have
            if (p + size > end) size = end - p;     // truncated tail — read what is there
            var cs = p + hdr, ce = p + size;

            if (type === 'moov') { seenMoov = true; walk(cs, ce, track); }
            else if (type === 'trak') {
                var t = { kind: '', timescale: 0, durationUnits: 0, sampleCount: 0 };
                walk(cs, ce, t);
                if (t.kind && t.timescale > 0 && !out[t.kind]) {
                    out[t.kind] = {
                        timescale: t.timescale,
                        durationSec: t.durationUnits / t.timescale,
                        sampleCount: t.sampleCount
                    };
                }
            }
            else if (CONTAINERS[type]) walk(cs, ce, track);
            else if (track) {
                if (type === 'hdlr' && cs + 12 <= ce) {
                    var h = str4(b, cs + 8);
                    track.kind = (h === 'vide') ? 'video' : (h === 'soun') ? 'audio' : '';
                }
                else if (type === 'mdhd') {
                    var ver = b[cs];
                    if (ver === 1 && cs + 32 <= ce) {
                        track.timescale = u32(b, cs + 20);
                        track.durationUnits = u32(b, cs + 24) * 4294967296 + u32(b, cs + 28);
                    } else if (cs + 20 <= ce) {
                        track.timescale = u32(b, cs + 12);
                        track.durationUnits = u32(b, cs + 16);
                    }
                    /* 0xFFFFFFFF is the "unknown duration" sentinel, not a length. */
                    if (track.durationUnits === 4294967295) track.durationUnits = 0;
                }
                else if (type === 'stsz' && cs + 12 <= ce) track.sampleCount = u32(b, cs + 8);
            }
            else if (type === 'mvhd') {
                var mver = b[cs];
                var mts = 0, mdu = 0;
                if (mver === 1 && cs + 28 <= ce) { mts = u32(b, cs + 20); mdu = u32(b, cs + 24) * 4294967296 + u32(b, cs + 28); }
                else if (cs + 20 <= ce) { mts = u32(b, cs + 12); mdu = u32(b, cs + 16); }
                if (mts > 0 && mdu > 0 && mdu !== 4294967295) out.movieDurationSec = mdu / mts;
            }
            p += size;
        }
    })(0, b.length, null);

    if (!seenMoov) return null;              // not an mp4, or moov never arrived
    if (!out.video && !out.audio) return null;
    return out;
};

if (typeof window.__drainSharedFunction === 'function') window.__drainSharedFunction('__mp4TrackSpans');
})();
